import useReports from './useReports';
import styles from './ReportsList.module.css';
import { ScrollView } from '@round/ui-kit';
import ReportsTable, { ReportsTableRow } from './ReportsTable/ReportsTable';
import useUrlState from 'Hooks/useUrlState';
import { SearchInput } from '@round/whitelabel-ui';
import { useMemo, useState } from 'react';
import { Button } from '@round/whitelabel-ui';
import { PageTitle } from '@round/whitelabel-ui';
import CreateReportModal from '../CreateReportModal/CreateReportModal';
import { useNavigate } from 'react-router-dom';
import EditReportModal from '../EditReportModal/EditReportModal';
import { creatorbase } from '@round/api';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import { Checkbox } from '@round/whitelabel-ui';
import DeleteReportModal from '../DeleteReportModal/DeleteReportModal';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useEffectWithPreviousDeps from 'Hooks/useEffectWithPreviousDeps';

type UrlState = {
    search: string;
    //boolean string
    myReports: string;
};

const ReportsList = () => {
    const { selectedClientId } = useClientSelect();

    const navigate = useNavigate();
    const [urlState, setUrlState] = useUrlState<UrlState>();

    const [isCreateReportOpen, setIsCreateReportOpen] = useState(false);
    const [isUpdateReportOpen, setIsUpdateReportOpen] = useState(false);
    const [reportToUpdate, setReportToUpdate] = useState<creatorbase.Report | null>(null);
    const [isDeleteReportOpen, setIsDeleteReportOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<creatorbase.Report | null>(null);

    const { creatorbaseUserId } = useCurrentCreatorbaseUser();
    const isMyReportsFilterActive = urlState.myReports === 'true';
    const assigneeIdFilter = isMyReportsFilterActive && !!creatorbaseUserId ? creatorbaseUserId.toString() : undefined;

    const reportsParams = useMemo(
        () => ({
            search: urlState.search,
            page_size: 25,
            assignee_ids: assigneeIdFilter,
            client_id: selectedClientId ?? undefined,
        }),
        [urlState.search, assigneeIdFilter, selectedClientId]
    );

    const { data, isPending, isAssigneesLoading, isFetchingNextPage, errorMessage, hasNextPage, fetchNextPage } =
        useReports(reportsParams);

    const rows: ReportsTableRow[] = useMemo(() => {
        return (
            data?.reports.map((report) => {
                return { ...report, assignees: data.assignees.filter((a) => report.assignee_ids.includes(a.id)) };
            }) ?? []
        );
    }, [data?.assignees, data?.reports]);

    const handleToggleMyReportsFilter = () => {
        setUrlState({ myReports: isMyReportsFilterActive ? undefined : 'true' });
    };

    useEffectWithPreviousDeps(
        (prev) => {
            const [prevClientId] = prev ?? [];
            if (prevClientId && prevClientId !== selectedClientId) {
                setUrlState({ search: '', myReports: undefined });
            }
        },
        [selectedClientId, setUrlState]
    );

    return (
        <>
            <div className={styles.container}>
                <header className={styles.header}>
                    <PageTitle>Reports</PageTitle>

                    <Button onClick={() => setIsCreateReportOpen(true)} appearance="primary">
                        Create Report
                    </Button>
                </header>

                <menu className={styles.menu}>
                    <SearchInput
                        className={styles.searchInput}
                        value={urlState.search}
                        onChange={(val) => {
                            setUrlState({ search: val });
                        }}
                    />

                    <div className={styles.myReportsFilter}>
                        <Checkbox value={isMyReportsFilterActive} onChange={handleToggleMyReportsFilter} />
                        <label onClick={handleToggleMyReportsFilter}>Only my reports</label>
                    </div>
                </menu>

                <ScrollView className={styles.tableContainer}>
                    <ReportsTable
                        data={rows}
                        hasError={!!errorMessage}
                        isLoading={isPending}
                        isAssigneesLoading={isAssigneesLoading}
                        onEditReport={(row) => {
                            setReportToUpdate(row);
                            setIsUpdateReportOpen(true);
                        }}
                        onDeleteReport={(row) => {
                            setReportToDelete(row);
                            setIsDeleteReportOpen(true);
                        }}
                        onEndReached={() => {
                            if (!isFetchingNextPage && hasNextPage) {
                                void fetchNextPage();
                            }
                        }}
                    />
                </ScrollView>
            </div>

            <CreateReportModal
                isOpen={isCreateReportOpen}
                onClose={() => setIsCreateReportOpen(false)}
                onCreated={(report) => {
                    // isEdit opens a project select modal
                    navigate(`${report.id}/?isEdit=true`);
                }}
            />

            <EditReportModal
                isOpen={isUpdateReportOpen}
                onClose={() => {
                    setIsUpdateReportOpen(false);
                    setReportToUpdate(null);
                }}
                report={reportToUpdate}
            />

            <DeleteReportModal
                isOpen={isDeleteReportOpen}
                onClose={() => {
                    setIsDeleteReportOpen(false);
                    setReportToDelete(null);
                }}
                report={reportToDelete}
            />
        </>
    );
};

export default ReportsList;
